import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fetchParticipants } from '../services/api';

const ParticipantsModal = ({ isOpen, onClose, contest }) => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalPages: 0,
    totalElements: 0
  });
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isSubmissionModalOpen, setIsSubmissionModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    submissionType: null,
    sortBy: 'likeCount',
    sortDirection: 'DESC'
  });

  useEffect(() => {
    if (isOpen && contest) {
      fetchParticipantsData();
    }
  }, [isOpen, contest, pagination.page, filters]);

  const fetchParticipantsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pagination.page,
        size: pagination.size,
        sortBy: filters.sortBy,
        sortDirection: filters.sortDirection,
        name: filters.name || null,
        email: filters.email || null,
        submissionType: filters.submissionType,
        contestId: contest.id // Using contest.id to filter participants by contest
      };
      
      const response = await fetchParticipants(params);
      
      setParticipants(response.content || []);
      setPagination(prev => ({
        ...prev,
        totalPages: response.totalPages || 0,
        totalElements: response.totalElements || 0
      }));
    } catch (err) {
      setError('Failed to fetch participants. Please try again.');
      console.error('Error fetching participants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({
      ...prev,
      page: 0
    }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const clearFilters = () => {
    setFilters({
      name: '',
      email: '',
      submissionType: null,
      sortBy: 'likeCount',
      sortDirection: 'DESC'
    });
  };

  const handleViewSubmission = (participant) => {
    setSelectedSubmission(participant);
    setIsSubmissionModalOpen(true);
  };

  const closeSubmissionModal = () => {
    setIsSubmissionModalOpen(false);
    setSelectedSubmission(null);
  };

  // Utility function to convert UTC to local time
  const convertUtcToLocal = (utcDateString) => {
    if (!utcDateString) return null;
    try {
      return new Date(utcDateString);
    } catch (error) {
      console.error('Error converting UTC to local:', error);
      return null;
    }
  };

  // Utility function to calculate time window (1 hour before and after scheduled time)
  const getTimeWindow = (scheduledTimeString) => {
    if (!scheduledTimeString) return { oneHourBefore: null, oneHourAfter: null };
    try {
      const scheduled = new Date(scheduledTimeString);
      const oneHourBefore = new Date(scheduled.getTime() - 60 * 60 * 1000);
      const oneHourAfter = new Date(scheduled.getTime() + 60 * 60 * 1000);
      return { oneHourBefore, oneHourAfter };
    } catch (error) {
      console.error('Error calculating time window:', error);
      return { oneHourBefore: null, oneHourAfter: null };
    }
  };

  // Utility function to format live stream time
  const formatLiveStreamTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return format(date, 'MMM dd, yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting live stream time:', error);
      return dateString;
    }
  };

  // Handler for joining live stream
  const handleJoinLiveStream = () => {
    // TODO: Implement join live stream functionality
    // This would typically open the live stream URL or navigate to the stream
    console.log('Join live stream clicked for submission:', selectedSubmission?.id);
    // You can add navigation or modal opening logic here
  };

  // Handler for watching recording
  const handleWatchRecording = () => {
    // TODO: Implement watch recording functionality
    // This would typically open the recording URL or play the recording
    console.log('Watch recording clicked for submission:', selectedSubmission?.id);
    // You can add video player or navigation logic here
  };

  // Handler for image click (full-screen viewer)
  const handleImagePress = () => {
    // Optional: Open full-screen image viewer
    // For now, we'll just log it
    console.log('Image clicked for full-screen view');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-xl font-semibold text-gray-900">
            Participants for {contest?.heading || contest?.title}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Filters */}
        <div className="border-b bg-gray-50">
          {/* Mobile Filter Toggle Button */}
          <div className="md:hidden border-b border-gray-200">
            <button
              type="button"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="w-full p-4 text-left flex justify-between items-center hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
            >
              <span className="text-sm font-medium text-gray-900">Filters</span>
              <svg
                className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isFilterOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Filter Form */}
          <div className={`${isFilterOpen ? 'block' : 'hidden'} md:block p-6`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search by name..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={filters.email}
                onChange={(e) => handleFilterChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search by email..."
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Clear Filters
              </button>
            </div>
          </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {error && (
            <div className="p-6 bg-red-100 border border-red-400 text-red-700">
              <p>{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : participants.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <p>No participants found.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contest
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Submission Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Reactions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Submitted At
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {participants.map((participant, index) => (
                      <tr key={participant.id || index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-12 w-12">
                              {participant.profilePicturePath ? (
                                <img
                                  className="h-12 w-12 rounded-full object-cover border-2 border-gray-200"
                                  src={participant.profilePicturePath}
                                  alt={`${participant.firstName || participant.email || 'User'} profile`}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className={`h-12 w-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold ${participant.profilePicturePath ? 'hidden' : 'flex'}`}>
                                <span className="text-lg">
                                  {participant.firstName?.charAt(0) || participant.email?.charAt(0) || 'U'}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {participant.firstName && participant.lastName 
                                  ? `${participant.firstName} ${participant.lastName}`
                                  : participant.email || 'Unknown User'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{participant.email || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{participant.contestHeading || 'N/A'}</div>
                          <div className="text-sm text-gray-500">{participant.contestCategory} - {participant.contestSubcategory}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {participant.submissionType || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex space-x-4 text-sm">
                            <div className="flex items-center text-green-600">
                              <span className="mr-1">👍</span>
                              <span>{participant.likeCount || 0}</span>
                            </div>
                            <div className="flex items-center text-red-600">
                              <span className="mr-1">👎</span>
                              <span>{participant.dislikeCount || 0}</span>
                            </div>
                            <div className="flex items-center text-yellow-600">
                              <span className="mr-1">😐</span>
                              <span>{participant.sosoCount || 0}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {participant.submissionDateTime ? new Date(participant.submissionDateTime).toLocaleDateString() : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleViewSubmission(participant)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            View Submission
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden">
                {participants.map((participant, index) => (
                  <div key={participant.id || index} className="bg-white border-b border-gray-200 p-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="flex-shrink-0 h-12 w-12">
                        {participant.profilePicturePath ? (
                          <img
                            className="h-12 w-12 rounded-full object-cover border-2 border-gray-200"
                            src={participant.profilePicturePath}
                            alt={`${participant.firstName || participant.email || 'User'} profile`}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className={`h-12 w-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold ${participant.profilePicturePath ? 'hidden' : 'flex'}`}>
                          <span className="text-lg">
                            {participant.firstName?.charAt(0) || participant.email?.charAt(0) || 'U'}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {participant.firstName && participant.lastName 
                            ? `${participant.firstName} ${participant.lastName}`
                            : participant.email || 'Unknown User'}
                        </div>
                        <div className="text-sm text-gray-500">{participant.email || 'N/A'}</div>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div><span className="font-medium">Contest:</span> {participant.contestHeading || 'N/A'}</div>
                      <div><span className="font-medium">Category:</span> {participant.contestCategory} - {participant.contestSubcategory}</div>
                      <div><span className="font-medium">Type:</span> {participant.submissionType || 'N/A'}</div>
                      <div>
                        <span className="font-medium">Reactions:</span>
                        <div className="flex space-x-3 mt-1">
                          <div className="flex items-center text-green-600">
                            <span className="mr-1">👍</span>
                            <span>{participant.likeCount || 0}</span>
                          </div>
                          <div className="flex items-center text-red-600">
                            <span className="mr-1">👎</span>
                            <span>{participant.dislikeCount || 0}</span>
                          </div>
                          <div className="flex items-center text-yellow-600">
                            <span className="mr-1">😐</span>
                            <span>{participant.sosoCount || 0}</span>
                          </div>
                        </div>
                      </div>
                      <div><span className="font-medium">Submitted:</span> {participant.submissionDateTime ? new Date(participant.submissionDateTime).toLocaleDateString() : 'N/A'}</div>
                      <div className="mt-3">
                        <button
                          onClick={() => handleViewSubmission(participant)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          View Submission
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Pagination */}
        {!loading && participants.length > 0 && (
          <div className="p-6 border-t bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {pagination.page * pagination.size + 1} to {Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)} of {pagination.totalElements} participants
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 0}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {pagination.page + 1} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages - 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Submission Viewer Modal */}
        {isSubmissionModalOpen && selectedSubmission && (
          <div className="fixed inset-0 z-60 flex items-center justify-center bg-black bg-opacity-75 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div className="flex justify-between items-center p-6 border-b">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    Submission by {selectedSubmission.firstName && selectedSubmission.lastName 
                      ? `${selectedSubmission.firstName} ${selectedSubmission.lastName}`
                      : selectedSubmission.email}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedSubmission.contestHeading} - {selectedSubmission.submissionType}
                  </p>
                </div>
                <button
                  onClick={closeSubmissionModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
                {selectedSubmission.submissionType === 'TEXT' && (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Text Submission</h4>
                    <div className="prose max-w-none">
                      <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {selectedSubmission.contentText || 'No text content available'}
                      </p>
                    </div>
                  </div>
                )}

                {selectedSubmission.submissionType === 'IMAGE' && (
                  <div className="text-center">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Image Submission</h4>
                    {selectedSubmission.filePath ? (
                      <img
                        src={selectedSubmission.filePath}
                        alt="Submission"
                        className="max-w-full max-h-96 rounded-lg shadow-lg mx-auto"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    ) : null}
                    <div className="hidden bg-gray-100 rounded-lg p-8 text-gray-500">
                      <p>Image could not be loaded</p>
                    </div>
                    {selectedSubmission.contentText && (
                      <div className="mt-4 bg-gray-50 rounded-lg p-4">
                        <h5 className="font-medium text-gray-700 mb-2">Description:</h5>
                        <p className="text-gray-600">{selectedSubmission.contentText}</p>
                      </div>
                    )}
                  </div>
                )}

                {selectedSubmission.submissionType === 'VIDEO' && (
                  <div className="text-center">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Video Submission</h4>
                    {selectedSubmission.filePath ? (
                      <video
                        controls
                        className="max-w-full max-h-96 rounded-lg shadow-lg mx-auto"
                        preload="metadata"
                      >
                        <source src={selectedSubmission.filePath} type="video/mp4" />
                        <source src={selectedSubmission.filePath} type="video/webm" />
                        <source src={selectedSubmission.filePath} type="video/ogg" />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <div className="bg-gray-100 rounded-lg p-8 text-gray-500">
                        <p>Video file not available</p>
                      </div>
                    )}
                    {selectedSubmission.videoPreview && (
                      <div className="mt-4">
                        <h5 className="font-medium text-gray-700 mb-2">Video Preview:</h5>
                        <img
                          src={selectedSubmission.videoPreview}
                          alt="Video Preview"
                          className="max-w-xs rounded-lg shadow mx-auto"
                        />
                      </div>
                    )}
                    {selectedSubmission.contentText && (
                      <div className="mt-4 bg-gray-50 rounded-lg p-4">
                        <h5 className="font-medium text-gray-700 mb-2">Description:</h5>
                        <p className="text-gray-600">{selectedSubmission.contentText}</p>
                      </div>
                    )}
                  </div>
                )}

                {selectedSubmission.submissionType === 'AUDIO' && (
                  <div className="text-center">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Audio Submission</h4>
                    {selectedSubmission.filePath ? (
                      <div className="bg-gray-50 rounded-lg p-6">
                        <audio
                          controls
                          className="w-full max-w-md mx-auto"
                          preload="metadata"
                        >
                          <source src={selectedSubmission.filePath} type="audio/mpeg" />
                          <source src={selectedSubmission.filePath} type="audio/wav" />
                          <source src={selectedSubmission.filePath} type="audio/ogg" />
                          Your browser does not support the audio tag.
                        </audio>
                      </div>
                    ) : (
                      <div className="bg-gray-100 rounded-lg p-8 text-gray-500">
                        <p>Audio file not available</p>
                      </div>
                    )}
                    {selectedSubmission.contentText && (
                      <div className="mt-4 bg-gray-50 rounded-lg p-4">
                        <h5 className="font-medium text-gray-700 mb-2">Description:</h5>
                        <p className="text-gray-600">{selectedSubmission.contentText}</p>
                      </div>
                    )}
                  </div>
                )}

                {selectedSubmission.submissionType === 'LIVEFEED' && (() => {
                  const scheduledTime = convertUtcToLocal(selectedSubmission.liveStreamScheduled);
                  const now = new Date();
                  
                  // Calculate time window: 1 hour before to 1 hour after scheduled time
                  const { oneHourBefore, oneHourAfter } = getTimeWindow(selectedSubmission.liveStreamScheduled);
                  
                  // Check if stream has ended (handle both boolean and string)
                  const streamEnded = selectedSubmission.isLiveStreamEnded === true || 
                    selectedSubmission.isLiveStreamEnded === 'true';
                  
                  // Check if current time is within the allowed window
                  // Note: isLiveStreamEnded takes precedence over time window
                  const isWithinWindow = !streamEnded && 
                    scheduledTime && oneHourBefore && oneHourAfter && 
                    now >= oneHourBefore && now <= oneHourAfter;
                  const hasNotStarted = !streamEnded && 
                    scheduledTime && oneHourBefore && now < oneHourBefore;
                  const hasEnded = streamEnded || 
                    (scheduledTime && oneHourAfter && now > oneHourAfter);
                  
                  return (
                    <div className="text-center">
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Live Feed Submission</h4>
                      
                      {/* Thumbnail Image Section */}
                      {selectedSubmission.filePath && (
                        <div className="mb-6">
                          <div 
                            className="relative cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={handleImagePress}
                          >
                            <div className="relative w-full max-w-2xl mx-auto">
                              <img
                                src={selectedSubmission.filePath}
                                alt="Live Feed Thumbnail"
                                className="w-full h-auto rounded-lg shadow-lg"
                                style={{ maxHeight: '400px', objectFit: 'cover' }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              {/* Live Badge Overlay */}
                              {isWithinWindow && !(selectedSubmission.isLiveStreamEnded === true || selectedSubmission.isLiveStreamEnded === 'true') && (
                                <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full flex items-center space-x-2 shadow-lg">
                                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                  <span className="text-sm font-bold">LIVE</span>
                                </div>
                              )}
                            </div>
                            <div className="hidden bg-gray-100 rounded-lg p-8 text-gray-500">
                              <p>Thumbnail could not be loaded</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Live Stream Information Section */}
                      <div className="bg-gray-50 rounded-lg p-6 mb-6">
                        <div className="flex items-start space-x-4 mb-4">
                          <svg 
                            className="w-6 h-6 text-gray-600 flex-shrink-0 mt-1" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="flex-1 text-left">
                            <p className="text-sm font-medium text-gray-700 mb-1">
                              {(selectedSubmission.isLiveStreamEnded === true || selectedSubmission.isLiveStreamEnded === 'true')
                                ? 'Stream Ended' 
                                : hasEnded 
                                  ? 'Stream Ended' 
                                  : hasNotStarted 
                                    ? 'Not Available' 
                                    : isWithinWindow 
                                      ? 'Live Now!' 
                                      : selectedSubmission.liveStreamScheduled
                                        ? 'Scheduled for'
                                        : 'No schedule available'}
                            </p>
                            {(selectedSubmission.isLiveStreamEnded === true || selectedSubmission.isLiveStreamEnded === 'true') && (
                              <p className="text-xs text-red-600 mb-1">Stream Ended</p>
                            )}
                            <p className="text-base text-gray-900 font-semibold">
                              {selectedSubmission.liveStreamScheduled 
                                ? formatLiveStreamTime(selectedSubmission.liveStreamScheduled)
                                : 'N/A'}
                            </p>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
                          {selectedSubmission.isLiveStreamEnded === true || selectedSubmission.isLiveStreamEnded === 'true' ? (
                            // Watch Recording Button (when stream ended)
                            <button
                              onClick={handleWatchRecording}
                              className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                            >
                              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Watch Recording
                            </button>
                          ) : (
                            // Join Live Stream Button (for viewers)
                            <button
                              onClick={handleJoinLiveStream}
                              disabled={!isWithinWindow}
                              className={`inline-flex items-center justify-center px-6 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                                isWithinWindow
                                  ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
                                  : 'bg-gray-400 text-white cursor-not-allowed focus:ring-gray-300'
                              }`}
                            >
                              <svg 
                                className="w-5 h-5 mr-2" 
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor"
                              >
                                {hasEnded ? (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                ) : hasNotStarted ? (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                ) : (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                )}
                              </svg>
                              {hasEnded 
                                ? 'Stream Ended' 
                                : hasNotStarted 
                                  ? 'Not Available' 
                                  : 'Join Live Stream'}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Description if available */}
                      {selectedSubmission.contentText && (
                        <div className="mt-4 bg-gray-50 rounded-lg p-4">
                          <h5 className="font-medium text-gray-700 mb-2">Description:</h5>
                          <p className="text-gray-600">{selectedSubmission.contentText}</p>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Submission Details */}
                <div className="mt-6 bg-gray-50 rounded-lg p-4">
                  <h5 className="font-medium text-gray-700 mb-3">Submission Details</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-600">Contest:</span>
                      <p className="text-gray-800">{selectedSubmission.contestHeading}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Category:</span>
                      <p className="text-gray-800">{selectedSubmission.contestCategory} - {selectedSubmission.contestSubcategory}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Submitted:</span>
                      <p className="text-gray-800">
                        {selectedSubmission.submissionDateTime 
                          ? new Date(selectedSubmission.submissionDateTime).toLocaleString() 
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Reactions:</span>
                      <div className="flex space-x-3 mt-1">
                        <div className="flex items-center text-green-600">
                          <span className="mr-1">👍</span>
                          <span>{selectedSubmission.likeCount || 0}</span>
                        </div>
                        <div className="flex items-center text-red-600">
                          <span className="mr-1">👎</span>
                          <span>{selectedSubmission.dislikeCount || 0}</span>
                        </div>
                        <div className="flex items-center text-yellow-600">
                          <span className="mr-1">😐</span>
                          <span>{selectedSubmission.sosoCount || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParticipantsModal; 